import React, { useEffect, useState } from 'react';
import { Timer, HelpCircle } from 'lucide-react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const PRESETS = [15, 30, 60, 120, 240, 480];

const extractError = (e: any) => e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Erro inesperado';

const formatDuracao = (minutos: number) => {
  if (minutos < 60) return `${minutos} min`;
  const horas = minutos / 60;
  return `${Number.isInteger(horas) ? horas : horas.toFixed(1)} h`;
};

const ConfiguracaoSessaoConfig: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [tempoSessaoMinutos, setTempoSessaoMinutos] = useState<number>(60);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/configuracao-sessao');
      setTempoSessaoMinutos(data.configuracao.tempoSessaoMinutos);
    } catch (e) {
      toast({ title: 'Erro', description: extractError(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSalvar = async () => {
    if (!tempoSessaoMinutos || tempoSessaoMinutos < 5 || tempoSessaoMinutos > 1440) {
      toast({ title: 'Atenção', description: 'Informe um tempo de sessão entre 5 e 1440 minutos (24 horas).', variant: 'destructive' });
      return;
    }

    setSalvando(true);
    try {
      await api.put('/configuracao-sessao', { tempoSessaoMinutos });
      toast({ title: 'Sucesso', description: 'Tempo de sessão salvo com sucesso.' });
    } catch (e) {
      toast({ title: 'Erro', description: extractError(e), variant: 'destructive' });
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-500 dark:text-gray-400">Carregando...</div>;
  }

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <h1 className="text-xl sm:text-2xl font-bold dark:text-white">Tempo de Sessão</h1>
          <HelpCircle className="h-4 w-4 text-gray-400" />
        </div>
        <Button onClick={handleSalvar} disabled={salvando}>
          {salvando ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
      <p className="text-gray-500 dark:text-gray-400 mb-6">
        Define por quanto tempo de inatividade um usuário pode ficar sem interagir com o sistema antes que sua sessão expire.
        Quando a sessão expira, o token de autenticação deixa de ser válido, o usuário é desconectado e passa a aparecer como
        Offline. Enquanto o usuário estiver ativo, a sessão é renovada automaticamente.
      </p>

      <Card className="mb-6 p-4 dark:bg-slate-800">
        <div className="flex items-center gap-2 mb-4">
          <Timer className="h-5 w-5" />
          <h2 className="font-semibold dark:text-white">Duração da sessão</h2>
        </div>

        <div className="flex flex-col gap-3 pl-1 max-w-sm">
          <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
            <Label className="dark:text-white dark:bg-slate-700">Tempo (minutos)</Label>
            <Input
              type="number"
              min={5}
              max={1440}
              value={tempoSessaoMinutos}
              onChange={(e) => setTempoSessaoMinutos(Number(e.target.value) || 0)}
              className="sm:col-span-3 dark:text-white dark:bg-slate-700"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
            <Label className="dark:text-white dark:bg-slate-700">Atalhos</Label>
            <div className="sm:col-span-3">
              <Select value={String(tempoSessaoMinutos)} onValueChange={(v) => setTempoSessaoMinutos(Number(v))}>
                <SelectTrigger className="dark:text-white dark:bg-slate-700">
                  <SelectValue placeholder="Selecione um valor comum" />
                </SelectTrigger>
                <SelectContent>
                  {PRESETS.map((minutos) => (
                    <SelectItem key={minutos} value={String(minutos)}>{formatDuracao(minutos)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Padrão: 60 minutos (1 hora). Valor atual: {formatDuracao(tempoSessaoMinutos)}.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ConfiguracaoSessaoConfig;
